import type { CSSProperties } from "react"

import type {
  BracketMatchItem,
  BracketMatchPlayerItem,
  BracketRound,
} from "@/domain/bracket/model/Bracket"

interface BracketTreeProps {
  rounds: BracketRound[]
}

interface BracketTreeMatchProps {
  match: BracketMatchItem
  /** 앞 라운드에서 올라온 자리인가 (1라운드는 들어오는 선이 없다) */
  hasIncomingLine: boolean
  /** 다음 라운드로 나가는 ㄱ 자 선의 방향 — 마지막 라운드면 없다 */
  outgoingLine?: "top" | "bottom" | "solo"
}

interface BracketTreeSlotProps {
  player: BracketMatchPlayerItem
  cornerLabel: string
  cornerClassName: string
}

/** 상위 라운드 자리 — 앞선 경기의 승자가 올라온다 (이 시스템은 결과를 다루지 않는다) */
const WINNER_SLOT_LABEL = "승자 진출"

/**
 * 토너먼트 트리.
 *
 * 라운드를 좌 → 우로 늘어놓고 경기 카드를 1px 헤어라인으로 이어 붙인다 —
 * 이음선은 `app-bracket-*` 클래스의 가상 요소라 좌표를 재는 스크립트가 없다.
 * 자리 높이가 고정이라 라운드마다 칸 높이가 2배씩 늘어나면서
 * 앞 라운드 두 칸의 경계가 다음 라운드 칸의 중앙과 맞물린다.
 * 넘겨 줄 값은 1라운드 경기 수(`--app-bracket-rows`) 하나뿐이다.
 *
 * 모바일 폭에서는 가로 스크롤 컨테이너 안에서 봐야 하므로 부모가 `overflow-x-auto` 를 준다.
 *
 * **대회 전에 짜는 표다.** 1라운드만 선수가 채워져 있고 상위 라운드는 골격이다 —
 * 종이 대진표처럼 **종목별 경기 번호**(종목마다 1부터)를 칸마다 달아 두어 빈 칸으로 보이지 않게 한다.
 * 부전승 자리는 붙을 상대가 없으니 상대 칸을 비워 두고 선만 이어 다음 라운드로 보낸다.
 * 승자 표시는 없다(경기 결과를 다루지 않는다).
 */
export default function BracketTree({ rounds }: Readonly<BracketTreeProps>) {
  const rowCount = Math.max(...rounds.map((round) => round.matches.length), 1)
  const treeStyle = { "--app-bracket-rows": rowCount } as CSSProperties

  return (
    <div
      className="app-bracket"
      style={treeStyle}
    >
      {rounds.map((round, roundIndex) => (
        <section
          key={round.roundNo}
          className="app-bracket-round"
          aria-label={`${round.roundName} 경기`}
        >
          <h3 className="app-bracket-round-head">{round.roundName}</h3>

          <ol className="app-bracket-body">
            {round.matches.map((match, matchIndex) => (
              <BracketTreeMatch
                key={match.id}
                match={match}
                hasIncomingLine={roundIndex > 0}
                outgoingLine={outgoingLineOf(
                  roundIndex,
                  rounds.length,
                  matchIndex,
                  round.matches.length,
                )}
              />
            ))}
          </ol>
        </section>
      ))}
    </div>
  )
}

/**
 * 칸에서 다음 라운드로 나가는 선의 방향.
 *
 * 짝수 번째 칸은 아래로(`top`), 홀수 번째 칸은 위로(`bottom`) 그어 칸 경계에서 만난다.
 * 짝이 없는 칸(2의 거듭제곱이 아닌 대진)은 세로 없이 가로로만 잇는다.
 */
function outgoingLineOf(
  roundIndex: number,
  roundCount: number,
  matchIndex: number,
  matchCount: number,
): "top" | "bottom" | "solo" | undefined {
  if (roundIndex >= roundCount - 1) {
    return undefined
  }

  if (matchIndex % 2 === 1) {
    return "bottom"
  }

  return matchIndex + 1 < matchCount ? "top" : "solo"
}

function BracketTreeMatch({
  match,
  hasIncomingLine,
  outgoingLine,
}: Readonly<BracketTreeMatchProps>) {
  return (
    <li
      className="app-bracket-cell"
      data-bracket-in={hasIncomingLine ? "true" : undefined}
      data-bracket-out={outgoingLine}
    >
      <div className="app-bracket-match">
        <p className="app-bracket-match-no">
          <span className="text-app-text-300">{match.orderNo}경기</span>
          {match.isBye && <span>부전승</span>}
        </p>

        <div className="app-bracket-card">
          <BracketTreeSlot
            player={match.homePlayer}
            cornerLabel="홍"
            cornerClassName="text-app-red-300"
          />

          {match.isBye ? (
            <p className="app-bracket-slot app-bracket-slot-empty app-txt-caption font-medium text-app-text-200">
              상대 없이 진출
            </p>
          ) : (
            <BracketTreeSlot
              player={match.awayPlayer}
              cornerLabel="청"
              cornerClassName="text-app-blue-300"
            />
          )}
        </div>
      </div>
    </li>
  )
}

function BracketTreeSlot({
  player,
  cornerLabel,
  cornerClassName,
}: Readonly<BracketTreeSlotProps>) {
  const hasPlayer = Boolean(player.name)

  return (
    <div
      className={
        hasPlayer
          ? "app-bracket-slot"
          : "app-bracket-slot app-bracket-slot-empty"
      }
    >
      <p
        className={`app-txt-body2 font-bold ${
          hasPlayer ? "text-app-text-300" : "text-app-text-200"
        }`}
      >
        <span className={`app-txt-caption mr-1.5 ${cornerClassName}`}>
          {cornerLabel}
        </span>
        {player.name || WINNER_SLOT_LABEL}
      </p>

      {player.gymName && (
        <p
          className="app-txt-caption font-medium text-app-text-200"
          title={player.gymName}
        >
          {player.gymName}
        </p>
      )}
    </div>
  )
}
